<template>
  <section class="page-interaction tasks-page">
    <!-- Görevler / Davet Kodları sekmeleri -->
    <div class="tasks-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="tasks-tab-btn"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <ReferralCodes v-if="activeTab === 'referral_codes'" />

    <!-- dupDevice true ise uyarı mesajı göster -->
    <div v-else-if="userDupDevice" class="dup-device-warning">
      <div class="dup-device-content">
        <feather-icon icon="AlertCircleIcon" size="48" class="warning-icon" />
        <h3>{{ $t('Access Denied') }}</h3>
        <p>{{ $t('You have already benefited from tasks on another account') }}</p>
      </div>
    </div>

    <b-overlay
      v-else
      :show="loading"
      no-center
      variant="transparent"
      rounded="sm"
    >
      <template #overlay>
        <loading-spinner />
      </template>

      <b-container fluid>
        <!-- Ücretsiz Mesajlaşma Banner -->
        <div class="free-messaging-banner">
          <div class="free-messaging-content">
            <div class="free-messaging-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="40" height="40" rx="8" fill="#1B1F2E" stroke="#F59E0B" stroke-width="2"/>
                <path d="M12 14C12 12.8954 12.8954 12 14 12H26C27.1046 12 28 12.8954 28 14V22C28 23.1046 27.1046 24 26 24H22L18 28V24H14C12.8954 24 12 23.1046 12 22V14Z" fill="#F59E0B"/>
                <circle cx="16" cy="18" r="1.5" fill="#1B1F2E"/>
                <circle cx="20" cy="18" r="1.5" fill="#1B1F2E"/>
                <circle cx="24" cy="18" r="1.5" fill="#1B1F2E"/>
              </svg>
            </div>
            <div class="free-messaging-text">
              <h4>{{ $t('Free Messaging') }}</h4>
              <p>{{ $t('Free messaging description') }}</p>
            </div>
          </div>
          <button class="free-messaging-btn" @click="onFreeMessagingClick">
            {{ $t('Start messaging') }} →
          </button>
        </div>

        <!-- Topladığın Elmaslar Header -->
        <div class="diamond-header-container">
          <div class="diamond-header">
            <span class="diamond-header-title">{{ $t('Collected Diamonds') }}</span>
            <div class="diamond-header-value">
              <!-- TODO: diamond-pink.png icon'unu ekle -->
              <img src="/assets/images/task/heart.png" width="28" height="28" alt="diamond" />
              <span>{{ numberFormatter(summary.total_credits || 0) }}</span>
            </div>
          </div>
          <div class="diamond-header-sub">
            <div class="diamond-header-sub-left">
              <feather-icon icon="LockIcon" size="14" />
              <span>{{ $t('Unlockable Diamonds') }}</span>
            </div>
            <div class="diamond-header-sub-right">
              <!-- TODO: diamond-pink.png icon'unu ekle -->
              <img src="/assets/images/task/heart.png" width="16" height="16" alt="diamond" />
              <span>{{ numberFormatter(convertSummary ? convertSummary.total_credits : 0) }}</span>
            </div>
          </div>
          <p class="diamond-header-desc">
            {{ $t('Unlock diamonds info') }}
          </p>
          <button
            v-if="convertStatus && convertStatus.can_convert"
            class="btn-primary-gradient"
            :disabled="convertLoading"
            @click="onConvertClick"
          >
            {{ $t('Convert Diamonds') }}
          </button>
          <button
            v-else
            class="btn-primary-gradient disabled"
            disabled
          >
            {{ $t('Convert Diamonds') }}
          </button>
        </div>

        <!-- Günlük Seri (Check-in) -->
        <div class="task-card">
          <h4 class="task-card-title">{{ $t('Daily Streak') }}</h4>
          <div ref="dailyStreakContainer" class="daily-streak-container">
            <div
              v-for="(day, index) in dayProgress"
              :key="index"
              :ref="isToday(day.date) ? 'todayStreak' : null"
              class="daily-streak-item"
              :class="{
                'completed': day.has_check_in,
                'current': isToday(day.date) && !day.has_check_in && !day.is_future,
                'locked': day.is_future
              }"
            >
              <div class="daily-streak-box" @click="onCheckInClick(day)">
                <!-- TODO: heart-pink.png icon'unu ekle -->
                <img src="/assets/images/task/heart.png" width="28" height="28" alt="heart" />
                <span class="daily-streak-value">{{ getCheckInReward(index) }}</span>
              </div>
              <span class="daily-streak-label">
                {{ day.has_check_in ? $t('Earned') : (isToday(day.date) && !day.is_future ? $t('Claim') : $t('Day') + ' ' + day.day) }}
              </span>
            </div>
          </div>
          <p class="task-card-desc">
            {{ $t('Daily streak description') }}
          </p>
        </div>

        <!-- Keyifli Zaman (Connect) -->
        <div class="task-card">
          <div class="enjoyable-time-header">
            <div class="enjoyable-time-left">
              <h4 class="task-card-title mb-1">{{ $t('Enjoyable Time') }}</h4>
              <p class="task-card-desc mb-0">
                {{ $t('Enjoyable time description') }}
              </p>
            </div>
            <div class="enjoyable-time-right">
              <div class="time-circle-wrapper" :class="{ 'success': isTimeCompleted }">
                <svg class="time-circle-svg" viewBox="0 0 100 100">
                  <defs>
                    <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stop-color="#F59E0B" />
                      <stop offset="100%" stop-color="#FBBF24" />
                    </linearGradient>
                    <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stop-color="#10B981" />
                      <stop offset="100%" stop-color="#34D399" />
                    </linearGradient>
                  </defs>
                  <!-- Base blue ring -->
                  <circle
                    class="base-ring"
                    cx="50"
                    cy="50"
                    r="45"
                  />
                  <!-- Gray background arc -->
                  <circle
                    class="bg-arc"
                    cx="50"
                    cy="50"
                    r="45"
                  />
                  <!-- Orange progress arc (only show when progress > 0) -->
                  <circle
                    v-if="timeProgress > 0"
                    class="progress-arc"
                    cx="50"
                    cy="50"
                    r="45"
                    :stroke-dasharray="progressArcDasharray"
                    stroke-dashoffset="0"
                  />
                </svg>
                <!-- Progress indicator dot -->
                <div
                  class="progress-dot"
                  :style="{ transform: `rotate(${progressDotAngle}deg)` }"
                >
                  <span class="dot"></span>
                </div>
                <span class="time-value">{{ formatDuration(todayConnectedDuration) }}</span>
              </div>
            </div>
          </div>
          <div class="enjoyable-time-days">
            <div
              v-for="(day, index) in dayProgress"
              :key="index"
              class="enjoyable-time-day"
              :class="{
                'completed': day.has_connect,
                'current': isToday(day.date) && !day.is_future,
                'locked': day.is_future
              }"
            >
              <img v-if="day.has_connect" src="/assets/images/task/time-check.png" width="48" height="32" alt="check" />
              <template v-else-if="isToday(day.date) && !day.is_future">
                <img src="/assets/images/task/heart.png" width="14" height="14" alt="heart" />
                <span>100</span>
              </template>
              <template v-else>
                <span>{{ $t('Day') }} {{ day.day }}</span>
              </template>
            </div>
            <!-- Bonus -->
            <div class="enjoyable-time-day bonus">
              <!-- TODO: heart-pink.png icon'unu ekle -->
              <img src="/assets/images/task/heart.png" width="14" height="14" alt="heart" />
              <span>{{ $t('Bonus') }}</span>
            </div>
          </div>
        </div>

        <!-- Günlük Video Moment -->
        <div class="task-card">
          <h4 class="task-card-title">{{ $t('Daily Video Moment') }}</h4>
          <p class="task-card-desc mb-2">
            {{ $t('Daily video moment description') }}
          </p>
          <div class="moment-days-container">
            <div
              v-for="(day, index) in dayProgress"
              :key="'moment-' + index"
              class="moment-day-item"
              :class="{
                'completed': hasMomentCompleted(day),
                'current': isToday(day.date) && !day.is_future && !hasMomentCompleted(day),
                'locked': day.is_future
              }"
              @click="onMomentDayClick(day)"
            >
              <template v-if="hasMomentCompleted(day)">
                <img src="/assets/images/task/time-check.png" width="40" height="28" alt="check" />
              </template>
              <template v-else-if="isToday(day.date) && !day.is_future">
                <img src="/assets/images/task/heart.png" width="18" height="18" alt="heart" />
                <span>100</span>
                <span class="moment-day-hint">{{ $t('Upload video') }}</span>
              </template>
              <template v-else>
                <span>{{ $t('Day') }} {{ day.day }}</span>
              </template>
            </div>
          </div>
          <p class="task-card-desc mt-2 mb-0">
            {{ summary.moments_count || 0 }}/{{ summary.moments_target || 7 }} {{ $t('days completed') }}
          </p>
          <p class="moment-rules-warning">
            {{ $t('Moment rules warning') }}
          </p>
        </div>

        <!-- Elmas Challenge (Deposit) -->
        <div class="task-card">
          <div class="diamond-challenge-header">
            <div class="diamond-challenge-left">
              <h4 class="task-card-title mb-1">{{ $t('Diamond Challenge') }}</h4>
              <p class="task-card-desc mb-0">
                {{ $t('Diamond challenge description') }}
              </p>
            </div>
            <div class="diamond-challenge-right">
              <img src="/assets/images/task/double-heart.png" width="70" height="54" alt="hearts" />
            </div>
          </div>
          <div class="diamond-challenge-progress-wrapper">
            <div class="diamond-challenge-progress">
              <div class="progress-circles">
                <div
                  v-for="(level, index) in challengeLevels.slice(0, 3)"
                  :key="index"
                  class="progress-circle"
                  :class="{
                    'completed': summary.deposit_total_credits >= level.threshold,
                    'current': summary.deposit_total_credits < level.threshold && (index === 0 || summary.deposit_total_credits >= challengeLevels[index - 1].threshold)
                  }"
                >
                  <img
                    v-if="summary.deposit_total_credits >= level.threshold"
                    src="/assets/images/task/task-stamp.png"
                    width="59"
                    height="59"
                    alt="completed"
                  />
                  <template v-else>
                    <img
                      src="/assets/images/task/task-stamp-empty.png"
                      width="59"
                      height="59"
                      alt="empty"
                    />
                    <span class="circle-number">{{ index + 1 }}</span>
                  </template>
                </div>
              </div>
              <div class="progress-bar-container">
                <span class="progress-text">{{ summary.deposit_count || 0 }}/3</span>
                <div class="progress-bar-segmented">
                  <div
                    v-for="i in 3"
                    :key="i"
                    class="progress-segment"
                    :class="{ 'filled': (summary.deposit_count || 0) >= i }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Elmas Challenge Seviyeleri -->
        <div class="task-card collapsible" :class="{ 'expanded': showLevels }">
          <div class="task-card-header" @click="showLevels = !showLevels">
            <h4 class="task-card-title mb-0">{{ $t('Diamond Challenge Levels') }}</h4>
            <feather-icon
              :icon="showLevels ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="20"
            />
          </div>
          <div v-if="showLevels" class="levels-container">
            <div
              v-for="(level, index) in challengeLevels"
              :key="index"
              class="level-row"
              :class="{ 'achieved': summary.deposit_total_credits >= level.threshold }"
            >
              <!-- TODO: diamond-yellow.png icon'unu ekle -->
              <div class="level-left">
                <img src="@/assets/images/icons/diamonds-1.png" width="24" height="20" alt="diamond" />
                <span>{{ numberFormatter(level.threshold) }}+</span>
              </div>
              <div class="level-right">
                
                <img src="/assets/images/task/heart.png" width="21" height="20" alt="heart" />
                <span>{{ numberFormatter(level.reward) }}</span>
              </div>
            </div>
          </div>
        </div>

      </b-container>
    </b-overlay>
  </section>
</template>

<script>
import {
  BContainer,
  BOverlay,
} from 'bootstrap-vue'
// eslint-disable-next-line import/no-cycle
import useJwt from '@/auth/jwt/useJwt'
import { throwDefaultError, numberFormatter } from '@/helpers/helpers'
import moment from 'moment'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ReferralCodes from '@/views/rewards/components/ReferralCodes.vue'

export default {
  name: 'TasksComponent',
  props: {
    mobile: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BContainer,
    BOverlay,
    ReferralCodes,
  },
  data() {
    return {
      activeTab: 'tasks',
      loading: false,
      convertLoading: false,
      showLevels: false,
      // API Data
      summary: {},
      dayProgress: [],
      convertStatus: null,
      convertSummary: null,
      // Challenge Levels (sabit değerler - backend'den gelebilir)
      challengeLevels: [
        { threshold: 5000, reward: 100 },
        { threshold: 10000, reward: 250 },
        { threshold: 15000, reward: 450 },
        { threshold: 20000, reward: 750 },
        { threshold: 25000, reward: 1100 },
        { threshold: 30000, reward: 1500 },
      ],
    }
  },
  computed: {
    tabs() {
      return [
        { key: 'tasks', label: this.$t('Tasks') },
        { key: 'referral_codes', label: this.$t('referrals.tab') },
      ]
    },
    todayConnectedDuration() {
      const today = this.dayProgress.find(d => this.isToday(d.date))
      // API'den saniye cinsinden geliyor, dakikaya çeviriyoruz
      return today ? today.connected_duration / 60 : 0
    },
    // 10 dakika tamamlandı mı?
    isTimeCompleted() {
      return this.todayConnectedDuration >= 10
    },
    // 10 dakika = 100%, progress yüzdesi
    timeProgress() {
      const maxMinutes = 10
      return Math.min((this.todayConnectedDuration / maxMinutes) * 100, 100)
    },
    // SVG circle circumference (2 * PI * r) where r = 45
    circleCircumference() {
      return 2 * Math.PI * 45 // ~282.74
    },
    // Orange progress arc dasharray
    progressArcDasharray() {
      const progress = this.timeProgress / 100
      const arcLength = this.circleCircumference * progress
      return `${arcLength} ${this.circleCircumference}`
    },
    // Gray arc dasharray (remaining part)
    grayArcDasharray() {
      const progress = this.timeProgress / 100
      const remaining = 1 - progress
      const arcLength = this.circleCircumference * remaining
      return `${arcLength} ${this.circleCircumference}`
    },
    // Gray arc offset (starts where orange ends)
    grayArcOffset() {
      const progress = this.timeProgress / 100
      return -this.circleCircumference * progress
    },
    // Progress dot angle (starts from top, goes clockwise)
    progressDotAngle() {
      // SVG -90deg döndürüldüğü için, arc üstten başlayıp saat yönünde ilerliyor
      // Dot da aynı şekilde üstten başlamalı
      return (this.timeProgress / 100) * 360
    },
    depositProgress() {
      if (!this.summary.deposit_total_credits) return 0
      const maxThreshold = this.challengeLevels[this.challengeLevels.length - 1].threshold
      return Math.min((this.summary.deposit_total_credits / maxThreshold) * 100, 100)
    },
    userFreeCallUser() {
      return this.$store.getters['auth/freeCallUser']
    },
    userDupDevice() {
      return this.$store.getters['auth/dupDevice']
    },
  },
  created() {
    // freeCallUser varsa ana sayfaya yönlendir
    if (this.userFreeCallUser) {
      this.$router.replace({ name: 'home' })
      return
    }
    // dupDevice true ise içeriği gösterme
    if (this.userDupDevice) {
      return
    }
    this.getData()
  },
  mounted() {
    // Kişinin bilgilerini getir
    useJwt.getMe()
      .then(response => {
        this.$store.dispatch('auth/updateUser', response.data.body)
      })
  },
  methods: {
    numberFormatter,
    getData() {
      this.loading = true

      useJwt.maleUserTasksWeeklyStatus()
        .then(response => {
          const { body } = response.data
          this.summary = body.summary || {}
          this.dayProgress = body.day_progress || []
          this.convertStatus = body.convert_status || null
          this.convertSummary = body.convert_summary || null
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.loading = false
          // Bugünün serisine scroll yap
          this.$nextTick(() => {
            this.scrollToTodayStreak()
          })
        })
    },
    scrollToTodayStreak() {
      const container = this.$refs.dailyStreakContainer
      const todayEl = this.$refs.todayStreak
      
      if (container && todayEl) {
        const element = Array.isArray(todayEl) ? todayEl[0] : todayEl
        if (element) {
          const scrollLeft = element.offsetLeft - (container.offsetWidth / 2) + (element.offsetWidth / 2)
          
          // Sadece scroll gerekiyorsa yap
          if (scrollLeft > 0) {
            container.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            })
          }
        }
      }
    },
    isToday(date) {
      return moment(date).isSame(moment(), 'day')
    },
    hasMomentCompleted(day) {
      const count = day.moments_count != null ? day.moments_count : 0
      const target = day.moments_target != null ? day.moments_target : 1
      return count >= target
    },
    onMomentDayClick(day) {
      // Yönlendirme yapılmıyor
      return
    },
    getCheckInReward(dayIndex) {
      // Her gün için check-in ödülü (örnek: 100)
      return 100
    },
    formatDuration(minutes) {
      const mins = Math.floor(minutes)
      const secs = Math.round((minutes - mins) * 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    onCheckInClick(day) {
      // Sadece bugün ve henüz check-in yapılmamışsa
      if (!this.isToday(day.date) || day.has_check_in || day.is_future) {
        return
      }

      this.loading = true

      useJwt.maleUserTasksCheckIn()
        .then(response => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('Check-in successful'),
            },
          })
          // Verileri yenile
          this.getData()
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
          this.loading = false
        })
    },
    onConvertClick() {
      if (!this.convertStatus || !this.convertStatus.can_convert) {
        return
      }

      this.convertLoading = true

      useJwt.maleUserTasksConvertWeekly()
        .then(response => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('Success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('Diamonds converted successfully'),
            },
          })
          // Verileri yenile
          this.getData()
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.convertLoading = false
        })
    },
    onFreeMessagingClick() {
      // Chat sayfasına yönlendir
      this.$router.push({ name: 'chat' })
    },
  },
}
</script>

<style lang="scss" scoped>
// Animasyonlar
@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0px -1px 0px 0px #88ECFD inset,
                0px 4px 3.5px 0px #8BB0ED inset,
                0 0 15px rgba(255, 74, 231, 0.4);
  }
  50% {
    box-shadow: 0px -1px 0px 0px #88ECFD inset,
                0px 4px 3.5px 0px #8BB0ED inset,
                0 0 25px rgba(255, 74, 231, 0.7);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes dot-pulse {
  0%, 100% {
    box-shadow: 0 0 8px rgba(251, 191, 36, 0.8);
  }
  50% {
    box-shadow: 0 0 16px rgba(251, 191, 36, 1);
  }
}

@keyframes heart-beat {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.tasks-page {
  min-height: 100vh;
  padding: 16px 0;

  // Görevler / Davet Kodları sekmeleri
  .tasks-tabs {
    display: flex;
    gap: 8px;
    max-width: 398px;
    margin: 0 auto 16px;
    padding: 4px;
    background: #1B1F2E;
    border: 1px solid #2D3857;
    border-radius: 15px;

    .tasks-tab-btn {
      flex: 1 1 0;
      padding: 10px 12px;
      border: 0;
      border-radius: 11px;
      background: transparent;
      color: rgba(255, 255, 255, 0.7);
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s, color 0.2s;

      &.active {
        background: linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%);
        color: #1B1F2E;
      }
    }
  }

  // Ücretsiz Mesajlaşma Banner
  .free-messaging-banner {
    background: linear-gradient(90deg, #1B1F2E 0%, #2D3857 27.44%, #2F3A59 51.34%, #2D3857 75.91%, #1B1F2E 100%);
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    max-width: 398px;
    margin-left: auto;
    margin-right: auto;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 15px;
      padding: 1px;
      background: linear-gradient(90deg, #272C3C 0%, #76819F 49.52%, #272C3C 100%);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }

    .free-messaging-content {
      display: flex;
      align-items: center;
      gap: 14px;
      flex: 1;
    }

    .free-messaging-icon {
      flex-shrink: 0;
      
      svg {
        display: block;
      }
    }

    .free-messaging-text {
      h4 {
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        margin: 0 0 4px 0;
      }

      p {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.6);
        margin: 0;
        line-height: 1.4;
      }
    }

    .free-messaging-btn {
      flex-shrink: 0;
      background: linear-gradient(180deg, #0996E6 22.5%, #70E3FF 100%);
      border: none;
      border-radius: 20px;
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      padding: 10px 16px;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      white-space: nowrap;
      box-shadow: 0px -1px 0.1px 0px #F2F9FC inset,
                  0px 1px 0px 0px #9CC4F2 inset,
                  0px 4px 0px 0px #426FB2;

      &:hover {
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(2px);
        box-shadow: 0px -1px 0.1px 0px #F2F9FC inset,
                    0px 1px 0px 0px #9CC4F2 inset,
                    0px 2px 0px 0px #426FB2;
      }
    }

    @media (max-width: 400px) {
      flex-direction: column;
      align-items: flex-start;

      .free-messaging-btn {
        width: 100%;
        text-align: center;
      }
    }
  }

  .dup-device-warning {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    padding: 20px;

    .dup-device-content {
      text-align: center;
      max-width: 300px;
      padding: 0 16px;

      .warning-icon {
        color: #F59E0B;
        margin-bottom: 16px;
      }

      h3 {
        color: #fff;
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 12px;
      }

      p {
        color: rgba(255, 255, 255, 0.6);
        font-size: 14px;
        line-height: 1.5;
        margin: 0;
      }

      @media (max-width: 576px) {
        h3 {
          font-size: 18px;
        }

        p {
          font-size: 13px;
        }
      }
    }
  }

  .diamond-header-container {
    background: linear-gradient(90deg, #1B1F2E 0%, #2D3857 27.44%, #2F3A59 51.34%, #2D3857 75.91%, #1B1F2E 100%);
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 398px;
    margin-left: auto;
    margin-right: auto;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 15px;
      padding: 1px;
      background: linear-gradient(90deg, #272C3C 0%, #76819F 49.52%, #272C3C 100%);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }

    .diamond-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      background: linear-gradient(90deg, #404A67 0%, #234982 32.69%, #234982 67.31%, #2E344B 100%);
      border: 1px solid;
      border-image-source: linear-gradient(180deg, rgba(95, 106, 129, 0.7) 0%, #29324F 100%);
      border-radius: 12px;
      padding: 12px 24px;
      margin-bottom: 12px;
      box-shadow: 0px 3px 0px 0px rgba(24, 27, 42, 0.66);
      position: relative;
      overflow: hidden;
      max-width: 371px;
      height: 48px;
      width: 100%;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 12px;
        padding: 1px;
        background: linear-gradient(180deg, rgba(95, 106, 129, 0.7) 0%, #29324F 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
      }

      .diamond-header-title {
        font-size: 18px;
        font-weight: 600;
        color: #fff;
      }

      .diamond-header-value {
        display: flex;
        align-items: center;
        gap: 6px;

        img {
          animation: heart-beat 1.5s ease-in-out infinite;
        }

        span {
          font-size: 20px;
          font-weight: 700;
          color: #fff;
        }
      }
    }

    .diamond-header-sub {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      margin-bottom: 4px;

      .diamond-header-sub-left {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #D4D4D4;
        font-size: 13px;
      }

      .diamond-header-sub-right {
        display: flex;
        align-items: center;
        gap: 4px;
        color: #fff;
        font-size: 14px;
        font-weight: 600;
      }
    }

    // 8px ve koyu zeminde #525F78 okunmuyordu
    .diamond-header-desc {
      font-size: 11px;
      color: rgba(226, 220, 255, 0.6);
      text-align: left;
      width: 100%;
      margin-bottom: 16px;
    }

    .btn-primary-gradient {
      background: linear-gradient(180deg, #0996E6 22.5%, #70E3FF 100%);
      border: none;
      border-radius: 10px;
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      width: 150px;
      height: 30px;
      box-shadow: 0px -1px 0.1px 0px #F2F9FC inset,
                  0px 1px 0px 0px #9CC4F2 inset,
                  0px 4px 0px 0px #426FB2;

      &:hover:not(.disabled) {
        transform: translateY(-2px);
      }

      &:active:not(.disabled) {
        transform: translateY(2px);
        box-shadow: 0px -1px 0.1px 0px #F2F9FC inset,
                    0px 1px 0px 0px #9CC4F2 inset,
                    0px 2px 0px 0px #426FB2;
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .task-card {
    background: linear-gradient(90deg, #1B1F2E 0%, #2D3857 27.44%, #2F3A59 51.34%, #2D3857 75.91%, #1B1F2E 100%);
    border-radius: 15px;
    padding: 16px;
    margin-bottom: 12px;
    max-width: 398px;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 15px;
      padding: 1px;
      background: linear-gradient(90deg, #272C3C 0%, #76819F 49.52%, #272C3C 100%);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }

    .task-card-title {
      font-size: 16px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 12px;
    }

    .task-card-desc {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.6);
      line-height: 1.4;
    }

    &.collapsible {
      .task-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }

  // Günlük Seri
  .daily-streak-container {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 8px;
    margin-bottom: 12px;

    &::-webkit-scrollbar {
      display: none;
    }

    .daily-streak-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-width: 80px;

      .daily-streak-box {
        width: 80px;
        height: 95px;
        background: linear-gradient(180deg, #3B5998 0%, #4A6FB3 100%);
        border-radius: 15px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
        position: relative;
        box-shadow: 0px -1px 0px 0px #88ECFD inset,
                    0px 4px 3.5px 0px #8BB0ED inset;
        cursor: pointer;

        .daily-streak-value {
          font-size: 16px;
          font-weight: 700;
          color: #fff;
        }
      }

      .daily-streak-label {
        width: 80px;
        height: 23px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        color: #333;
        background: #8BA4C4;
        border-bottom-left-radius: 15px;
        border-bottom-right-radius: 15px;
        margin-top: -15px;
        position: relative;
        z-index: 1;
        box-shadow: 0px -1px 0px 0px #B0CED3 inset;
      }

      // Kazanıldı durumu
      &.completed {
        .daily-streak-box {
          background: linear-gradient(180deg, #1B71E1 0%, #FF4AE7 100%);
          box-shadow: 0px -1px 0px 0px #88ECFD inset,
                      0px 4px 3.5px 0px #8BB0ED inset;
        }
        .daily-streak-label {
          background: #BCDEFF;
          color: #1B71E1;
          font-weight: 600;
          box-shadow: 0px -1px 0px 0px #B0CED3 inset;
        }
      }

      // Kazan (bugün) durumu
      &.current {
        .daily-streak-box {
          background: linear-gradient(180deg, #1B71E1 0%, #FF4AE7 100%);
          animation: pulse-glow 2s ease-in-out infinite;
          cursor: pointer;

          img {
            animation: float 2s ease-in-out infinite;
          }
        }
        .daily-streak-label {
          background: linear-gradient(180deg, #C026D3 0%, #DB2777 100%);
          color: #fff;
          font-weight: 600;
        }
      }

      // Kilitli (gelecek günler) durumu
      &.locked {
        .daily-streak-box {
          background: linear-gradient(180deg, #3B5998 0%, #4A6FB3 100%);
          opacity: 0.8;
        }
        .daily-streak-label {
          background: #8BA4C4;
          color: #fff;
        }
      }
    }
  }

  // Keyifli Zaman
  .enjoyable-time-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;

    .enjoyable-time-left {
      flex: 1;
      padding-right: 16px;
    }

    .enjoyable-time-right {
      .time-circle-wrapper {
        width: 100px;
        height: 100px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .time-circle-svg {
          width: 100%;
          height: 100%;
          transform: rotate(-90deg);

          .base-ring {
            fill: none;
            stroke: #3B82F6;
            stroke-width: 3;
          }

          .bg-arc {
            fill: none;
            stroke: #9CA3AF;
            stroke-width: 8;
            stroke-linecap: round;
            transition: stroke-dasharray 0.3s ease, stroke-dashoffset 0.3s ease;
          }

          .progress-arc {
            fill: none;
            stroke: url(#orangeGradient);
            stroke-width: 8;
            stroke-linecap: round;
            transition: stroke-dasharray 0.3s ease;
          }
        }

        .progress-dot {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          pointer-events: none;
          transition: transform 0.3s ease;

          .dot {
            position: absolute;
            top: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 12px;
            height: 12px;
            background: #FFEA97;
            border-radius: 50%;
            box-shadow: 0 0 8px rgba(255, 234, 151, 1);
            animation: dot-pulse 1.5s ease-in-out infinite;
          }
        }

        .time-value {
          position: absolute;
          font-size: 22px;
          font-weight: 700;
          color: #fff;
        }

        // Success durumu (10dk tamamlandı)
        &.success {
          .time-circle-svg {
            .base-ring {
              stroke: #10B981;
            }

            .bg-arc {
              stroke: transparent;
            }

            .progress-arc {
              stroke: url(#successGradient);
            }
          }

          .progress-dot .dot {
            background: #34D399;
            box-shadow: 0 0 12px rgba(16, 185, 129, 0.8);
          }
        }
      }
    }
  }

  .enjoyable-time-days {
    display: flex;
    gap: 3px;
    flex-wrap: wrap;

    .enjoyable-time-day {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 40px;
      height: 25px;
      border-radius: 18.5px 18.5px 21px 21px;
      font-size: 9px;
      color: rgba(255, 255, 255, 0.6);
      background: linear-gradient(90deg, #404A67 0%, #234982 32.69%, #234982 67.31%, #2E344B 100%);
      box-shadow: 0px 2px 0px 0px rgba(24, 27, 42, 0.66);
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 18.5px 18.5px 21px 21px;
        padding: 1px;
        background: linear-gradient(180deg, rgba(95, 106, 129, 0.7) 0%, #29324F 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
      }

      &.completed {
        background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
        color: #fff;

        &::before {
          background: linear-gradient(180deg, rgba(100, 150, 255, 0.7) 0%, #3b5998 100%);
        }
      }

      &.current {
        background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
        color: #fff;

        &::before {
          background: linear-gradient(180deg, rgba(167, 139, 250, 0.7) 0%, #6d28d9 100%);
        }
      }

      &.bonus {
        background: linear-gradient(180deg, #3076E2 0%, #7062E3 34.62%, #C653E5 100%);
        color: #fff;
        font-weight: 600;
        width: 63px;
        box-shadow: 0px 2px 0px 0px rgba(24, 27, 42, 0.66);

        &::before {
          background: linear-gradient(180deg, rgba(95, 106, 129, 0.7) 0%, #29324F 100%);
        }
      }
    }
  }

  // Günlük Video Moment
  .moment-days-container {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 4px;

    .moment-day-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      min-width: 44px;
      padding: 8px 4px;
      border-radius: 12px;
      font-size: 10px;
      color: rgba(255, 255, 255, 0.6);
      background: linear-gradient(90deg, #404A67 0%, #234982 32.69%, #234982 67.31%, #2E344B 100%);
      box-shadow: 0px 2px 0px 0px rgba(24, 27, 42, 0.66);
      cursor: pointer;
      position: relative;
      transition: transform 0.2s, box-shadow 0.2s;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 12px;
        padding: 1px;
        background: linear-gradient(180deg, rgba(95, 106, 129, 0.7) 0%, #29324F 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
      }

      .moment-day-hint {
        font-size: 10px;
        opacity: 0.9;
      }

      &.completed {
        background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
        color: #fff;
        cursor: default;

        &::before {
          background: linear-gradient(180deg, rgba(100, 150, 255, 0.7) 0%, #3b5998 100%);
        }
      }

      &.current {
        background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
        color: #fff;
        cursor: pointer;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
        }

        &::before {
          background: linear-gradient(180deg, rgba(167, 139, 250, 0.7) 0%, #6d28d9 100%);
        }
      }

      &.locked {
        opacity: 0.8;
        cursor: default;
      }
    }
  }

  .moment-rules-warning {
    font-size: 10px;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 8px;
    margin-bottom: 0;
  }

  // Elmas Challenge
  .diamond-challenge-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;

    .diamond-challenge-left {
      flex: 1;
      padding-right: 16px;
    }

    .diamond-challenge-right {
      position: relative;

      img {
        filter: drop-shadow(0 0 10px rgba(236, 72, 153, 0.5));
        animation: heart-beat 1.5s ease-in-out infinite;
      }
    }
  }

  .diamond-challenge-progress-wrapper {
    background: linear-gradient(90deg, #404A67 0%, #234982 32.69%, #234982 67.31%, #2E344B 100%);
    border-radius: 5px 5px 21px 21px;
    padding: 16px;
    position: relative;
    box-shadow: 0px 3px 0px 0px rgba(24, 27, 42, 0.66);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: 5px 5px 21px 21px;
      padding: 1px;
      background: linear-gradient(180deg, rgba(95, 106, 129, 0.7) 0%, #29324F 100%);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
  }

  .diamond-challenge-progress {
    .progress-circles {
      display: flex;
      justify-content: space-around;
      margin-bottom: 12px;

      .progress-circle {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.4);
        background: transparent;
        border: none;
        position: relative;

        img {
          width: 59px;
          height: 59px;
          object-fit: contain;
        }

        .circle-number {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 18px;
          font-weight: 700;
          color: #FFFFFF;
        }

        &.completed {
          img {
            filter: none;
          }
        }

        &.current {
          img {
            opacity: 0.7;
          }
        }
      }
    }

    .progress-bar-container {
      display: flex;
      align-items: center;
      gap: 12px;

      .progress-text {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.6);
        min-width: 30px;
      }

      .progress-bar-segmented {
        flex: 1;
        display: flex;
        gap: 4px;

        .progress-segment {
          flex: 1;
          height: 8px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 4px;
          transition: background 0.3s ease;

          &.filled {
            background: linear-gradient(90deg, #0996E7 0%, #68DDFD 100%);
          }
        }
      }
    }
  }

  // Levels
  .levels-container {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    .level-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);

      &:last-child {
        border-bottom: none;
      }

      &.achieved {
        opacity: 0.5;
      }

      .level-left,
      .level-right {
        display: flex;
        align-items: center;
        gap: 8px;

        span {
          font-size: 15px;
          color: #fff;
          font-weight: 500;
        }
      }

      .level-left span {
        color: #fbbf24;
      }

      .level-right {
        min-width: 80px;

        img {
          flex-shrink: 0;
        }

        span {
          color: #f472b6;
          text-align: left;
        }
      }
    }
  }
}
</style>
